import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {Alert, Badge, Button, Form, Spinner, Table} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {KTCard, KTCardBody, Datepicker} from '../../../components'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {
  digitalPlanetFetchInvoices,
  digitalPlanetGetPdf,
  clearError,
  selectDigitalPlanetInvoiceList,
  selectDigitalPlanetFetchLoading,
  selectDigitalPlanetImportLoading,
  selectDigitalPlanetError,
} from '../../../redux/slices/digitalplanetinvoice.slice'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {ProjectAssignModal} from './ProjectAssignModal'
import {ImportResultModal} from './ImportResultModal'
import {PdfPreviewModal} from './PdfPreviewModal'

const today = new Date().toISOString().slice(0, 10)
const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)

const INVOICE_TYPE_LABELS = {
  SATIS: 'Satış Faturası',
  IADE: 'İade Faturası',
  TEVKIFAT: 'Tevkifat Faturası',
  ISTISNA: 'İstisna Faturası',
  OZELMATRAH: 'Özel Matrah',
  IHRACKAYITLI: 'İhraç Kayıtlı',
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
}

function formatAmount(amount) {
  if (amount == null) return '-'
  return Number(amount).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

function SortIcon({column, sortConfig}) {
  if (sortConfig.key !== column)
    return <span className='text-muted ms-1' style={{fontSize: 11}}>↕</span>
  return (
    <span className='ms-1' style={{fontSize: 11}}>
      {sortConfig.dir === 'asc' ? '↑' : '↓'}
    </span>
  )
}

function List() {
  const dispatch = useDispatch()
  const invoiceList = useSelector(selectDigitalPlanetInvoiceList)
  const fetchLoading = useSelector(selectDigitalPlanetFetchLoading)
  const importLoading = useSelector(selectDigitalPlanetImportLoading)
  const error = useSelector(selectDigitalPlanetError)

  const [startDate, setStartDate] = useState(
    () => sessionStorage.getItem('dp_startDate') || thirtyDaysAgo
  )
  const [endDate, setEndDate] = useState(
    () => sessionStorage.getItem('dp_endDate') || today
  )
  const [filterError, setFilterError] = useState(null)
  const [importError, setImportError] = useState(null)
  const [selectedUuids, setSelectedUuids] = useState(new Set())
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [pdfUuid, setPdfUuid] = useState(null)
  const [hideImported, setHideImported] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [sortConfig, setSortConfig] = useState({key: null, dir: 'asc'})

  const selectAllRef = useRef(null)

  useEffect(() => {
    dispatch(projectLookup())
  }, [dispatch])

  const validate = useCallback(() => {
    if (!startDate || !endDate) {
      setFilterError('Başlangıç ve bitiş tarihi zorunludur.')
      return false
    }
    if (endDate < startDate) {
      setFilterError('Bitiş tarihi başlangıç tarihinden küçük olamaz.')
      return false
    }
    const diff = (new Date(endDate) - new Date(startDate)) / 86400000
    if (diff > 90) {
      setFilterError('Tarih aralığı en fazla 90 gün olabilir.')
      return false
    }
    setFilterError(null)
    return true
  }, [startDate, endDate])

  const handleFetch = useCallback(() => {
    if (!validate()) return
    setSelectedUuids(new Set())
    dispatch(clearError())
    dispatch(digitalPlanetFetchInvoices({startDate, endDate}))
  }, [dispatch, startDate, endDate, validate])

  useEffect(() => {
    dispatch(clearError())
    dispatch(digitalPlanetFetchInvoices({startDate, endDate}))
  }, []) // intentional: fetch once on mount with initial date range

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') handleFetch()
    },
    [handleFetch]
  )

  const setStartDatePersisted = useCallback((val) => {
    setStartDate(val)
    sessionStorage.setItem('dp_startDate', val)
  }, [])

  const setEndDatePersisted = useCallback((val) => {
    setEndDate(val)
    sessionStorage.setItem('dp_endDate', val)
  }, [])

  const importableList = useMemo(
    () => (invoiceList || []).filter((inv) => !inv.isAlreadyImported),
    [invoiceList]
  )

  const allImportableSelected = useMemo(
    () => importableList.length > 0 && importableList.every((inv) => selectedUuids.has(inv.uuid)),
    [importableList, selectedUuids]
  )

  const someImportableSelected = useMemo(
    () => importableList.some((inv) => selectedUuids.has(inv.uuid)),
    [importableList, selectedUuids]
  )

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someImportableSelected && !allImportableSelected
    }
  }, [someImportableSelected, allImportableSelected])

  const handleSelectAll = useCallback(() => {
    if (allImportableSelected) {
      setSelectedUuids(new Set())
    } else {
      setSelectedUuids(new Set(importableList.map((inv) => inv.uuid)))
    }
  }, [allImportableSelected, importableList])

  const handleToggle = useCallback((uuid, isImported) => {
    if (isImported) return
    setSelectedUuids((prev) => {
      const next = new Set(prev)
      next.has(uuid) ? next.delete(uuid) : next.add(uuid)
      return next
    })
  }, [])

  const handleImportDone = useCallback((result) => {
    setShowProjectModal(false)
    setImportResult(result)
    setShowResultModal(true)
  }, [])

  const handleImportError = useCallback((err) => {
    setShowProjectModal(false)
    setImportError(typeof err === 'string' ? err : 'Aktarım sırasında bir hata oluştu.')
  }, [])

  const handleResultClose = useCallback(() => {
    setShowResultModal(false)
    setSelectedUuids(new Set())
    dispatch(digitalPlanetFetchInvoices({startDate, endDate}))
  }, [dispatch, startDate, endDate])

  const handlePdfPreview = useCallback(
    (uuid) => {
      setPdfUuid(uuid)
      dispatch(digitalPlanetGetPdf(uuid))
    },
    [dispatch]
  )

  const handleSort = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key ? {key, dir: prev.dir === 'asc' ? 'desc' : 'asc'} : {key, dir: 'asc'}
    )
  }, [])

  const selectedItems = useMemo(
    () => (invoiceList || []).filter((inv) => selectedUuids.has(inv.uuid)),
    [invoiceList, selectedUuids]
  )

  const displayList = useMemo(() => {
    let list = invoiceList || []
    if (hideImported) list = list.filter((inv) => !inv.isAlreadyImported)
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      list = list.filter(
        (inv) =>
          (inv.invoiceId || '').toLowerCase().includes(q) ||
          (inv.senderName || '').toLowerCase().includes(q) ||
          (inv.senderTaxId || '').toLowerCase().includes(q)
      )
    }
    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        const aVal = a[sortConfig.key] ?? ''
        const bVal = b[sortConfig.key] ?? ''
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        return sortConfig.dir === 'asc' ? cmp : -cmp
      })
    }
    return list
  }, [invoiceList, hideImported, searchText, sortConfig])

  // Stats always reflect what the user currently sees (displayList), not the raw fetch
  const stats = useMemo(() => {
    if (!invoiceList) return null
    const fetchedTotal = invoiceList.length
    const displayTotal = displayList.length
    const displayImported = displayList.filter((inv) => inv.isAlreadyImported).length
    const displayPending = displayTotal - displayImported
    const isFiltered = displayTotal !== fetchedTotal

    const amountByCurrency = displayList.reduce((acc, inv) => {
      const cur = inv.currency || 'TRY'
      acc[cur] = (acc[cur] || 0) + (inv.payableAmount || 0)
      return acc
    }, {})

    return {fetchedTotal, displayTotal, displayImported, displayPending, isFiltered, amountByCurrency}
  }, [invoiceList, displayList])

  const sortableHeader = useCallback(
    (label, key, className = '') => (
      <th
        key={key}
        className={className}
        style={{cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap'}}
        onClick={() => handleSort(key)}
      >
        {label}
        <SortIcon column={key} sortConfig={sortConfig} />
      </th>
    ),
    [handleSort, sortConfig]
  )

  return (
    <>
      <PageTitle>E-Faturalar</PageTitle>
      <KTCard>
        {/* ── Date filter section (API-level) ───────────────────────────────── */}
        <div className='card-header border-0 pt-6' onKeyDown={handleKeyDown}>
          <div className='card-title flex-column align-items-start gap-1'>
            <div className='d-flex align-items-end gap-3 flex-wrap'>
              <Form.Group>
                <Form.Label className='fw-bold fs-6'>Başlangıç Tarihi</Form.Label>
                <Datepicker
                  value={startDate ? new Date(startDate + 'T00:00:00') : ''}
                  onChange={([date]) => {
                    if (date) {
                      const y = date.getFullYear()
                      const m = String(date.getMonth() + 1).padStart(2, '0')
                      const d = String(date.getDate()).padStart(2, '0')
                      setStartDatePersisted(`${y}-${m}-${d}`)
                    }
                  }}
                  disabled={fetchLoading}
                  placeholder='gg.AA.yyyy'
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className='fw-bold fs-6'>Bitiş Tarihi</Form.Label>
                <Datepicker
                  value={endDate ? new Date(endDate + 'T00:00:00') : ''}
                  onChange={([date]) => {
                    if (date) {
                      const y = date.getFullYear()
                      const m = String(date.getMonth() + 1).padStart(2, '0')
                      const d = String(date.getDate()).padStart(2, '0')
                      setEndDatePersisted(`${y}-${m}-${d}`)
                    }
                  }}
                  disabled={fetchLoading}
                  placeholder='gg.AA.yyyy'
                />
              </Form.Group>
              <Button
                variant='primary'
                onClick={handleFetch}
                disabled={fetchLoading}
                className='align-self-end'
              >
                {fetchLoading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Getiriliyor...
                  </>
                ) : (
                  'Faturaları Getir'
                )}
              </Button>
            </div>
            {/* Inline date validation error — shown directly under the inputs */}
            {filterError && (
              <div className='d-flex align-items-center gap-2 text-danger fs-7 mt-1'>
                <i className='bi bi-exclamation-circle text-danger' />
                {filterError}
              </div>
            )}
          </div>
        </div>

        <KTCardBody>
          {error && (
            <Alert variant='danger' onClose={() => dispatch(clearError())} dismissible>
              {error}
            </Alert>
          )}
          {importError && (
            <Alert variant='danger' onClose={() => setImportError(null)} dismissible>
              {importError}
            </Alert>
          )}

          {fetchLoading && <ListLoading />}

          {!fetchLoading && invoiceList && (
            <>
              {/* ── Stats bar — reflects current displayList, not raw fetch ── */}
              {stats && (
                <div className='mb-4'>
                  <div className='d-flex gap-3 flex-wrap'>
                    <div className='border border-dashed rounded px-4 py-2 text-center min-w-80px'>
                      <div className='fw-bold fs-4'>{stats.displayTotal}</div>
                      <div className='text-muted fs-7'>Toplam</div>
                    </div>

                    {/* Bekliyor — clickable to toggle hide-imported filter */}
                    <div
                      role='button'
                      title={hideImported ? 'Filtreyi kaldır' : 'Sadece bekleyenleri göster'}
                      onClick={() => setHideImported((v) => !v)}
                      className={`border border-dashed rounded px-4 py-2 text-center min-w-80px ${
                        hideImported ? 'border-warning bg-light-warning' : ''
                      }`}
                      style={{cursor: 'pointer'}}
                    >
                      <div className='fw-bold fs-4 text-warning'>{stats.displayPending}</div>
                      <div className='text-muted fs-7'>
                        Bekliyor {hideImported && <i className='bi bi-funnel-fill fs-8 text-warning' />}
                      </div>
                    </div>

                    <div className='border border-dashed rounded px-4 py-2 text-center min-w-80px'>
                      <div className='fw-bold fs-4 text-success'>{stats.displayImported}</div>
                      <div className='text-muted fs-7'>Aktarıldı</div>
                    </div>

                    {/* One card per currency — no mixed-currency summing */}
                    {Object.entries(stats.amountByCurrency).map(([currency, amount]) => (
                      <div
                        key={currency}
                        className='border border-dashed rounded px-4 py-2 text-center min-w-80px'
                      >
                        <div className='fw-bold fs-4'>{formatAmount(amount)}</div>
                        <div className='text-muted fs-7'>Tutar ({currency})</div>
                      </div>
                    ))}
                  </div>

                  {/* Sub-note when client-side filters reduce the displayed count */}
                  {stats.isFiltered && (
                    <div className='text-muted fs-7 mt-2'>
                      <i className='bi bi-info-circle me-1' />
                      {stats.fetchedTotal} faturanın {stats.displayTotal} tanesi gösteriliyor
                    </div>
                  )}
                </div>
              )}

              {/* ── Table-level filter bar + import action ───────────────────── */}
              <div className='d-flex align-items-center justify-content-between gap-4 mb-4 flex-wrap'>
                <div className='d-flex align-items-center gap-4 flex-wrap'>
                  <div className='d-flex align-items-center gap-3'>
                    <Form.Control
                      type='text'
                      placeholder='Fatura no, firma adı veya VKN...'
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{maxWidth: 280}}
                    />
                    <Form.Check
                      type='switch'
                      id='hide-imported-switch'
                      label='Sadece Bekleyenler'
                      checked={hideImported}
                      onChange={(e) => setHideImported(e.target.checked)}
                    />
                  </div>
                </div>

                {/* Selection summary + import action — lives next to the table */}
                <div className='d-flex align-items-center gap-3'>
                  {selectedUuids.size > 0 && (
                    <span className='badge badge-light-primary fs-7 px-3 py-2'>
                      {selectedUuids.size} fatura seçildi
                    </span>
                  )}
                  <Button
                    variant='success'
                    onClick={() => setShowProjectModal(true)}
                    disabled={selectedUuids.size === 0 || importLoading}
                    title={
                      selectedUuids.size === 0
                        ? 'En az bir fatura seçin'
                        : `${selectedUuids.size} fatura aktarılacak`
                    }
                  >
                    {importLoading ? (
                      <>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Aktarılıyor...
                      </>
                    ) : (
                      `İçeri Aktar${selectedUuids.size > 0 ? ` (${selectedUuids.size})` : ''}`
                    )}
                  </Button>
                </div>
              </div>

              {/* ── Table ────────────────────────────────────────────────────── */}
              <div className='table-responsive'>
                <Table hover className='table align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted bg-light'>
                      <th className='ps-4 rounded-start'>
                        <input
                          ref={selectAllRef}
                          type='checkbox'
                          checked={allImportableSelected}
                          onChange={handleSelectAll}
                          disabled={importableList.length === 0}
                        />
                      </th>
                      {sortableHeader('Fatura No', 'invoiceId')}
                      {sortableHeader('Gönderen Firma', 'senderName')}
                      {sortableHeader('Gönderen VKN', 'senderTaxId')}
                      {sortableHeader('Fatura Tarihi', 'issueDate')}
                      {sortableHeader('Tutar', 'payableAmount', 'text-end')}
                      {sortableHeader('Para Birimi', 'currency')}
                      {sortableHeader('Fatura Tipi', 'invoiceTypeCode')}
                      <th>Durum</th>
                      <th className='rounded-end text-center'>Önizleme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayList.length === 0 && (
                      <tr>
                        <td colSpan={10} className='text-center text-muted py-6'>
                          {searchText || hideImported
                            ? 'Filtrelerle eşleşen fatura bulunamadı.'
                            : 'Bu tarih aralığında fatura bulunamadı.'}
                        </td>
                      </tr>
                    )}
                    {displayList.map((inv) => (
                      <tr
                        key={inv.uuid}
                        className={inv.isAlreadyImported ? 'bg-light text-muted' : ''}
                        style={{
                          opacity: inv.isAlreadyImported ? 0.65 : 1,
                          cursor: inv.isAlreadyImported ? 'default' : 'pointer',
                        }}
                        onClick={() => handleToggle(inv.uuid, inv.isAlreadyImported)}
                      >
                        <td className='ps-4'>
                          <input
                            type='checkbox'
                            checked={selectedUuids.has(inv.uuid)}
                            onChange={() => handleToggle(inv.uuid, inv.isAlreadyImported)}
                            disabled={inv.isAlreadyImported}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{inv.invoiceId || '-'}</td>
                        <td>{inv.senderName || '-'}</td>
                        <td>{inv.senderTaxId || '-'}</td>
                        <td>{formatDate(inv.issueDate)}</td>
                        <td className='text-end'>{formatAmount(inv.payableAmount)}</td>
                        <td>{inv.currency || '-'}</td>
                        <td>
                          {INVOICE_TYPE_LABELS[inv.invoiceTypeCode] || inv.invoiceTypeCode || '-'}
                        </td>
                        <td>
                          {inv.isAlreadyImported ? (
                            inv.expenseInvoiceId ? (
                              <Link
                                to={`/expenses/expenses/detail/detail/${inv.expenseInvoiceId}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Badge bg='success' style={{cursor: 'pointer'}}>
                                  Aktarıldı
                                </Badge>
                              </Link>
                            ) : (
                              <Badge bg='success'>Aktarıldı</Badge>
                            )
                          ) : (
                            <Badge bg='secondary'>Bekliyor</Badge>
                          )}
                        </td>
                        <td className='text-center'>
                          <Button
                            variant='light-primary'
                            size='sm'
                            title='PDF Önizle'
                            onClick={(e) => {
                              e.stopPropagation()
                              handlePdfPreview(inv.uuid)
                            }}
                          >
                            <i className='bi bi-file-pdf fs-5' />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </KTCardBody>
      </KTCard>

      {showProjectModal && (
        <ProjectAssignModal
          show={showProjectModal}
          selectedItems={selectedItems}
          onDone={handleImportDone}
          onError={handleImportError}
          onClose={() => setShowProjectModal(false)}
        />
      )}

      {showResultModal && importResult && (
        <ImportResultModal
          show={showResultModal}
          result={importResult}
          onClose={handleResultClose}
        />
      )}

      {pdfUuid && <PdfPreviewModal uuid={pdfUuid} onClose={() => setPdfUuid(null)} />}
    </>
  )
}

export {List}
